/**
 * TransactionService - Single Source of Truth for Transaction Calculations
 * 
 * This service provides centralized transaction calculation logic to ensure perfect
 * consistency between the on-screen display and JSON export functionality.
 * 
 * Key Benefits:
 * - Eliminates data discrepancies between UI and export
 * - Centralizes complex transaction logic in one location
 * - Provides consistent formatting and calculation rules
 * - Simplifies maintenance and debugging
 * 
 * Architecture:
 * - Used by EinzelSimulationTab.tsx for screen display
 * - Used by Schritt3.tsx for JSON export generation
 * - Implements identical calculation algorithms for both contexts
 * 
 * @author Portfolio-3Topf Team
 * @version 2.0.0
 * @since 2024-12-28 - Refactored from duplicated logic
 */

// Transaction data interfaces
interface TransactionYearData {
  // Common properties
  jahr?: number;
  alter?: number;
  istKrise?: boolean;
  
  // Verbrauchstopf properties
  verbrauchstopfSaldo?: number;
  verbrauchstopf?: number;
  initialVerbrauchstopfSaldo?: number;
  originalVerbrauchstopfSaldo?: number;
  verbrauchstopfInterest?: number;
  entnahmeVerbrauchstopf?: number;
  verbrauchstopfRefillAmount?: number;
  topf1AuffuellQuelle?: string;
  
  // Zinstopf properties
  zinstopfSaldo?: number;
  zinstopf?: number;
  originalZinstopfSaldo?: number;
  initialZinstopfSaldo?: number;
  zinstopfInterest?: number;
  entnahmeZinstopf?: number;
  zinstopfRefillAmount?: number;
  zinstopfCashFlow?: number;
  topf2AuffuellAktion?: string;
  
  // Investitionstopf properties
  investitionstopfSaldo?: number;
  investitionstopf?: number;
  originalInvestitionstopfSaldo?: number;
  initialInvestitionstopfSaldo?: number;
  investitionstopfInterest?: number;
  investitionstopfWithdrawal?: number;
  investitionstopfRefillAmount?: number;
  entnahmeInvestitionstopf?: number;
  investitionstopfCashFlow?: number;
}

/**
 * Transaction calculation result interface
 * 
 * Contains all transaction details and calculated values for a specific pot
 * in a standardized format used by both UI display and JSON export.
 */
export interface TransactionData {
  /** Array of formatted transaction strings for display */
  transactions: string[];
  /** Starting balance at beginning of year */
  startBalance: number;
  /** Ending balance at end of year */
  endBalance: number;
  /** Interest earned during the year */
  interestEarned: number;
}

export class TransactionService {
  /**
   * Calculate Verbrauchstopf (Consumption Pot) transactions for display
   * 
   * Handles the complex logic for consumption pot transactions including:
   * - Initial balance setup
   * - Refill calculations from other pots during crises
   * - Withdrawal processing
   * - Interest calculations
   * 
   * @param currentYearData - Current year simulation data
   * @param previousYearData - Previous year data (null for first year)
   * @param isFirstYear - Whether this is the first simulation year
   * @returns TransactionData with formatted transactions and balances
   */
  static calculateVerbrauchstopfTransactions(
    currentYearData: TransactionYearData,
    previousYearData: TransactionYearData | null,
    isFirstYear: boolean
  ): TransactionData {
    const transactions: string[] = [];
    
    // Start balance - for first year use initial balance, for subsequent years use previous year's ending balance
    const startBalance = isFirstYear
      ? (currentYearData.initialVerbrauchstopfSaldo || currentYearData.verbrauchstopfSaldo || 0)
      : (previousYearData?.verbrauchstopf || previousYearData?.verbrauchstopfSaldo || 0);
    
    // 01.01 Startvalue in Black
    transactions.push(`01.01.: ${Math.round(startBalance).toLocaleString('de-DE')} €`);
    
    // Calculate actual end balance based on transactions of current year
    // endBalance should be: startBalance + refills + interest - withdrawals
    const interestEarned = currentYearData.verbrauchstopfInterest ?? 0;
    
    // Calculate total refill amount (what comes INTO Verbrauchstopf from other pots)
    const totalRefillAmount = currentYearData.verbrauchstopfRefillAmount || 0;
    
    // Use actual Verbrauchstopf withdrawal, not total withdrawal
    const verbrauchstopfWithdrawal = currentYearData.entnahmeVerbrauchstopf || 0;
    
    // Calculate incoming overflow from Zinstopf (Überschuss)
    const incomingOverflow = (currentYearData as any).zinstopfUeberschussTransfer || 0;
    
    // Calculate correct end balance based on actual transactions
    // NOTE: incomingOverflow MUST be added here as it represents money flowing into Verbrauchstopf during the current year
    const calculatedEndBalance = startBalance + totalRefillAmount + incomingOverflow + interestEarned - verbrauchstopfWithdrawal;
    
    // + (aus Zinstopf or Investitionstopf) in Green - Refill to meet withdrawal target (can happen in any year)
    if (totalRefillAmount > 0.01) {
      // Use the detailed source information from the simulation engine
      let refillSource = 'Investitionstopf'; // Default fallback
      
      if (currentYearData.topf1AuffuellQuelle) {
        const source = currentYearData.topf1AuffuellQuelle;
        if (source.includes('Zinstopf + Investitionstopf')) {
          refillSource = 'Zinstopf + Investitionstopf';
        } else if (source.includes('Investitionstopf + Zinstopf')) {
          refillSource = 'Investitionstopf + Zinstopf';
        } else if (source.includes('Investitionstopf (Zinstopf leer)')) {
          // Special case: Zinstopf is empty, Investitionstopf is used as emergency source
          refillSource = 'Investitionstopf';
        } else if (source.includes('Zinstopf')) {
          refillSource = 'Zinstopf';
        } else if (source.includes('Investitionstopf')) {
          refillSource = 'Investitionstopf';
        }
      } else {
        // Fallback to old logic if no detailed source available
        refillSource = currentYearData.istKrise ? 'Zinstopf' : 'Investitionstopf';
      }
      
      transactions.push(`+ (aus ${refillSource}) ${Math.round(totalRefillAmount).toLocaleString('de-DE')} €`);
    }
    
    // + (Überschuss aus Zinstopf) in Green - show incoming overflow from Zinstopf if it occurred
    // NOTE: Overflow transfers can happen in ANY year, including the first year
    if (incomingOverflow > 0.01) {
      transactions.push(`+ (Überschuss aus Zinstopf) ${Math.round(incomingOverflow).toLocaleString('de-DE')} €`);
    }
    
    // - (Entnahme) value in Red - show actual Verbrauchstopf withdrawal
    if (verbrauchstopfWithdrawal > 0.01) {
      transactions.push(`- (Entnahme) ${Math.round(verbrauchstopfWithdrawal).toLocaleString('de-DE')} €`);
    }
    
    // +/- (Rendite) value in Red or Green - Interest earned during the year
    if (Math.abs(interestEarned) >= 0.01) {
      const sign = interestEarned > 0 ? '+' : '-';
      transactions.push(`${sign} (Rendite) ${Math.abs(Math.round(interestEarned)).toLocaleString('de-DE')} €`);
    } else if (interestEarned === 0) {
      // Explicitly show 0 interest for clarity in cases like Year 63
      transactions.push(`+ (Rendite) 0 €`);
    }
    
    // 31.12. Endvalue in Black - use calculated balance based on transactions
    transactions.push(`31.12.: ${Math.round(calculatedEndBalance).toLocaleString('de-DE')} €`);
    
    return {
      transactions,
      startBalance,
      endBalance: calculatedEndBalance,
      interestEarned
    };
  }
  
  /**
   * Calculate Zinstopf (Interest Pot) transactions for display
   * 
   * Manages the interest pot transactions including:
   * - Balance transfers to consumption pot during crises
   * - Interest accumulation at fixed rates
   * - Balance tracking and formatting
   * 
   * @param currentYearData - Current year simulation data
   * @param previousYearData - Previous year data (null for first year)
   * @param isFirstYear - Whether this is the first simulation year
   * @returns TransactionData with formatted transactions and balances
   */
  static calculateZinstopfTransactions(
    currentYearData: TransactionYearData,
    previousYearData: TransactionYearData | null,
    isFirstYear: boolean
  ): TransactionData {
    const transactions: string[] = [];
    
    // Start balance - for first year use initial balance, for subsequent years use previous year's ending balance
    const startBalance = isFirstYear
      ? (currentYearData.initialZinstopfSaldo || currentYearData.zinstopfSaldo || 0)
      : (previousYearData?.zinstopf || previousYearData?.zinstopfSaldo || 0);
    
    // 01.01 Startvalue in Black
    transactions.push(`01.01.: ${Math.round(startBalance).toLocaleString('de-DE')} €`);
    
    // Calculate actual end balance based on transactions
    const interestEarned = currentYearData.zinstopfInterest ?? 0;
    const cashFlow = currentYearData.zinstopfCashFlow || 0;

    // Direct user withdrawal from Zinstopf (occurs in depletion years when the
    // Verbrauchstopf cannot cover the full withdrawal target)
    const directWithdrawal = currentYearData.entnahmeZinstopf || 0;

    // Calculate outgoing overflow to Verbrauchstopf (Überschuss)
    const outgoingOverflow = (currentYearData as any).zinstopfUeberschussTransfer || 0;

    // Calculate correct end balance: start + inflows + interest - outflows
    const calculatedEndBalance = startBalance + Math.max(0, cashFlow) + interestEarned - Math.abs(Math.min(0, cashFlow)) - outgoingOverflow - directWithdrawal;

    // + (aus Investitionstopf) in Green - NOT in first year
    if (!isFirstYear && cashFlow > 0.01) {
      transactions.push(`+ (aus Investitionstopf) ${Math.round(cashFlow).toLocaleString('de-DE')} €`);
    }

    // - (in Verbrauchstopf) in Red - NOT in first year
    if (!isFirstYear && cashFlow < -0.01) {
      transactions.push(`- (in Verbrauchstopf) ${Math.abs(Math.round(cashFlow)).toLocaleString('de-DE')} €`);
    }

    // - (Überschuss an Verbrauchstopf) in Red - show overflow transfer if it occurred
    if (!isFirstYear && outgoingOverflow > 0.01) {
      transactions.push(`- (Überschuss an Verbrauchstopf) ${Math.round(outgoingOverflow).toLocaleString('de-DE')} €`);
    }

    // - (Entnahme) in Red - direct user withdrawal from Zinstopf (depletion years)
    if (directWithdrawal > 0.01) {
      transactions.push(`- (Entnahme) ${Math.round(directWithdrawal).toLocaleString('de-DE')} €`);
    }
    
    // +/- (Rendite) value in Red or Green - LAST transaction before end balance
    // Always show interest for transparency, even if 0
    if (Math.abs(interestEarned) >= 0.01) {
      const sign = interestEarned > 0 ? '+' : '-';
      transactions.push(`${sign} (Rendite) ${Math.abs(Math.round(interestEarned)).toLocaleString('de-DE')} €`);
    } else if (interestEarned === 0) {
      // Show 0 interest when starting balance is 0 (like Year 66 Zinstopf)
      transactions.push(`+ (Rendite) 0 €`);
    }
    
    // 31.12. Endvalue in Black - use calculated balance
    transactions.push(`31.12.: ${Math.round(calculatedEndBalance).toLocaleString('de-DE')} €`);
    
    return {
      transactions,
      startBalance,
      endBalance: calculatedEndBalance,
      interestEarned
    };
  }
  
  /**
   * Calculate Investitionstopf (Investment Pot) transactions for display
   * 
   * Handles investment pot transactions including:
   * - Market-based return calculations (positive and negative)
   * - Balance transfers to consumption pot during non-crisis periods
   * - Volatile return tracking and display
   * - Crisis vs. normal market condition handling
   * 
   * @param currentYearData - Current year simulation data
   * @param previousYearData - Previous year data (null for first year)
   * @param isFirstYear - Whether this is the first simulation year
   * @returns TransactionData with formatted transactions and balances
   */
  static calculateInvestitionstopfTransactions(
    currentYearData: TransactionYearData,
    previousYearData: TransactionYearData | null,
    isFirstYear: boolean
  ): TransactionData {
    const transactions: string[] = [];
    
    // Start balance - for first year use initial balance, for subsequent years use previous year's ending balance
    const startBalance = isFirstYear
      ? (currentYearData.initialInvestitionstopfSaldo || currentYearData.investitionstopfSaldo || 0)
      : (previousYearData?.investitionstopf || previousYearData?.investitionstopfSaldo || 0);
    
    // 01.01 Startvalue in Black
    transactions.push(`01.01.: ${Math.round(startBalance).toLocaleString('de-DE')} €`);
    
    // Calculate actual end balance based on transactions
    const interestEarned = currentYearData.investitionstopfInterest ?? 0;

    // Internal transfer to Verbrauchstopf (refill) - engine convention: negative = outflow
    const investitionstopfToVerbrauchstopf = Math.abs(
      Math.min(0, currentYearData.investitionstopfCashFlow || 0)
    );
    // Direct user withdrawal from Investitionstopf (occurs in the remainingNeed phase
    // of depletion years - the engine DOES withdraw directly from the Investitionstopf)
    const directWithdrawal = currentYearData.entnahmeInvestitionstopf || 0;
    const zinstopfCashFlow = Math.max(0, currentYearData.zinstopfCashFlow || 0);

    // Calculate correct end balance: start + interest - outflows to other pots - direct withdrawals
    const calculatedEndBalance = startBalance + interestEarned - investitionstopfToVerbrauchstopf - zinstopfCashFlow - directWithdrawal;

    // - (in Verbrauchstopf) in Red - show actual Investitionstopf contribution
    const refillSource = currentYearData.topf1AuffuellQuelle || '';

    if (!isFirstYear && investitionstopfToVerbrauchstopf > 0.01) {
      // Only show refill transaction if Investitionstopf is actually the source
      if (refillSource.includes('Investitionstopf')) {
        transactions.push(`- (in Verbrauchstopf) ${Math.round(investitionstopfToVerbrauchstopf).toLocaleString('de-DE')} €`);
      }
    }

    // - (Entnahme) in Red - direct user withdrawal from Investitionstopf (depletion years)
    if (directWithdrawal > 0.01) {
      transactions.push(`- (Entnahme) ${Math.round(directWithdrawal).toLocaleString('de-DE')} €`);
    }

    // - (in Zinstopf) in Red - NOT in first year AND only if actual transfer occurred
    // Check if this was a real transfer from Investitionstopf to Zinstopf (not just overflow transfer)
    const zinstopfAction = currentYearData.topf2AuffuellAktion || '';
    const isRealZinstopfTransfer = !isFirstYear && zinstopfCashFlow > 0.01 && !zinstopfAction.includes('Optimal gefüllt');
    if (isRealZinstopfTransfer) {
      transactions.push(`- (in Zinstopf) ${Math.round(zinstopfCashFlow).toLocaleString('de-DE')} €`);
    }
    
    // +/- (Rendite) value in Red or Green
    // Always show interest for transparency, even if 0
    if (Math.abs(interestEarned) >= 0.01) {
      const sign = interestEarned > 0 ? '+' : '-';
      transactions.push(`${sign} (Rendite) ${Math.abs(Math.round(interestEarned)).toLocaleString('de-DE')} €`);
    } else if (interestEarned === 0) {
      // Show 0 interest for clarity
      transactions.push(`+ (Rendite) 0 €`);
    }
    
    // 31.12. Endvalue in Black - use calculated balance
    transactions.push(`31.12.: ${Math.round(calculatedEndBalance).toLocaleString('de-DE')} €`);
    
    return {
      transactions,
      startBalance,
      endBalance: calculatedEndBalance,
      interestEarned
    };
  }
}

